import React, { FC, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyle,
  Checkbox,
  Losenge,
  Table,
} from '@versita/frontend/campus-ui-web';
import { toast } from 'react-hot-toast';
import { setSelectedIds } from '../../state/slices/timetableManagerSlice';
import { DateTime } from 'luxon';
import { usePublish } from '../../hooks/usePublish';
import { useAppDispatch, useTimetableManagerState } from '../../state';

interface AdminTableProps {
  isLoading?: boolean;
}

export const AdminTable: FC<AdminTableProps> = ({ isLoading }) => {
  const FIELDS = {
    SELECTED: 'selected',
    COURSE_NAME: 'courseName',
    COURSE_ID: 'courseId',
    COURSE_YEAR: 'year',
    PUBLISHED_DATE: 'publishedDate',
    PUBLISHED: 'published',
    ACTIONS: 'actions',
  };

  const FIELD_TO_HEADER = {
    [FIELDS.SELECTED]: 'Selected',
    [FIELDS.COURSE_NAME]: 'Course Name',
    [FIELDS.COURSE_ID]: 'Course Id',
    [FIELDS.COURSE_YEAR]: 'Course Year',
    [FIELDS.PUBLISHED_DATE]: 'Last Published',
    [FIELDS.PUBLISHED]: 'Published',
    [FIELDS.ACTIONS]: '',
  };

  const dispatch = useAppDispatch();
  const { filteredInstances, selectedIds } = useTimetableManagerState();
  const toggleSelection = (id: string) => {
    if (selectedIds.includes(id)) {
      dispatch(
        setSelectedIds(selectedIds.filter((existingId) => existingId !== id)),
      );
    } else {
      dispatch(setSelectedIds([...selectedIds, id]));
    }
  };
  const { handlePublishStatus, isUpdating } = usePublish();
  const getPublicationStatus = (
    willNotPublish: boolean,
    published: boolean,
  ) => {
    if (willNotPublish) {
      return 'Disabled';
    } else if (published) {
      return 'Published';
    } else {
      return 'Unpublished';
    }
  };

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedInstances = React.useMemo(() => {
    if (!sortField) {
      return filteredInstances;
    }
    const sorted = [...filteredInstances].sort((a, b) => {
      if (sortField === 'selected') {
        const aIsSelected = selectedIds.includes(a.id);
        const bIsSelected = selectedIds.includes(b.id);

        // If one is selected and the other isn't, sort accordingly
        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;

        // If both are either selected or not selected, you can return 0 (or sort based on a secondary criterion, like name or ID)
        return 0;
      }
      return a[sortField] > b[sortField] ? 1 : -1;
    });
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInstances, sortField, sortDirection]);

  return (
    <section aria-label="Course Timetable Instances">
      <Table
        loading={isLoading}
        updating={isUpdating}
        emptyText="No results found"
        headers={Object.entries(FIELDS).map(([key, value]) => ({
          key: value,
          display: FIELD_TO_HEADER[value],
        }))}
        sortField={sortField}
        sortDirection={sortDirection}
        sortHandlers={{
          [FIELDS.SELECTED]: () => handleSort(FIELDS.SELECTED),
          [FIELDS.COURSE_NAME]: () => handleSort(FIELDS.COURSE_NAME),
          [FIELDS.COURSE_ID]: () => handleSort(FIELDS.COURSE_ID),
          [FIELDS.COURSE_YEAR]: () => handleSort(FIELDS.COURSE_YEAR),
          [FIELDS.PUBLISHED_DATE]: () => handleSort(FIELDS.PUBLISHED_DATE),
          [FIELDS.PUBLISHED]: () => handleSort(FIELDS.PUBLISHED),
        }}
        data={sortedInstances.map((instance) => {
          const {
            id,
            courseId,
            publishedDate,
            year,
            courseName,
            published,
            willNotPublish,
          } = instance;
          return [
            <Checkbox
              key={id}
              id={id}
              label={`Select "${courseName}" Year: ${year}`}
              onChange={() => toggleSelection(id)}
              checked={selectedIds.includes(id)}
              labelVisible={false}
            />,
            courseName,
            courseId.replace(/\./g, '/'),
            year.toString(),
            publishedDate
              ? DateTime.fromISO(publishedDate).toFormat('dd/MM/yyyy')
              : '-',
            <Losenge
              style={{
                fontSize: '1.2rem',
                minWidth: '12rem',
                textAlign: 'center',
              }}
              status={
                (
                  {
                    Published: 'success',
                    Unpublished: 'warning',
                    Disabled: 'error',
                  } as const
                )[getPublicationStatus(willNotPublish, published)]
              }
            >
              {getPublicationStatus(willNotPublish, published)}
            </Losenge>,
            <div>
              <Button
                $buttonStyle={
                  willNotPublish ? ButtonStyle.Primary : ButtonStyle.Secondary
                }
                $buttonSize={ButtonSize.Small}
                style={{
                  minWidth: '6rem',
                  width: 'calc(50% - 0.4rem)',
                  marginRight: '0.8rem',
                  marginBottom: 0,
                  fontSize: '1.3rem',
                }}
                onClick={() =>
                  toast.promise(
                    handlePublishStatus(instance, 'willNotPublish'),
                    {
                      loading: willNotPublish ? 'Enabling...' : 'Disabling...',
                      success: willNotPublish
                        ? 'Timetable Enabled'
                        : 'Timetable Disabled',
                      error: willNotPublish
                        ? 'Could not enable'
                        : 'Could not disable',
                    },
                  )
                }
              >
                {`${willNotPublish ? 'Enable' : 'Disable'}`}
              </Button>
              <Button
                $buttonSize={ButtonSize.Small}
                $buttonStyle={ButtonStyle.Primary}
                style={{
                  minWidth: '6rem',
                  width: 'calc(50% - 0.4rem)',
                  marginBottom: 0,
                  fontSize: '1.3rem',
                }}
                disabled={willNotPublish}
                onClick={() =>
                  toast.promise(
                    handlePublishStatus(
                      instance,
                      published ? 'unpublished' : 'published',
                    ),
                    {
                      loading: published ? 'Unpublishing...' : 'Publishing...',
                      success: published
                        ? 'Timetable Unpublished'
                        : 'Timetable Published',
                      error: published
                        ? 'Could not unpublish'
                        : 'Could not publish',
                    },
                  )
                }
              >
                {published ? 'Unpublish' : 'Publish'}
              </Button>
            </div>,
          ];
        })}
      />
    </section>
  );
};
